<template>
  <div class="bg-white rounded-lg border border-gray-200 p-4 mb-6">
    <h3 class="text-sm font-semibold mb-3 text-gray-900">
      Step 3: Configure Entity Import
    </h3>

    <div class="space-y-4">
      <!-- EntityType Selection -->
      <MappedSelect
        label="EntityType *"
        :model-value="config.entity_type_id"
        :options="entityTypeOptions"
        placeholder="-- Select EntityType --"
        numeric
        @update:model-value="handleEntityTypeChange"
      />

      <!-- Entity Name Column -->
      <div>
        <MappedSelect
          label="Entity name column *"
          :model-value="config.entity_name_column"
          :options="sheetColumns"
          placeholder="-- Select column --"
          @update:model-value="updateConfig('entity_name_column', $event)"
        />
        <p class="text-xs text-gray-500 mt-1">Column containing the entity name</p>
      </div>

      <!-- New Entity Identifier -->
      <div class="p-3 bg-gray-50 rounded border border-gray-100">
        <h4 class="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">
          New entity identifier *
        </h4>
        <div class="grid grid-cols-2 gap-3">
          <MappedSelect
            label="Identifier column"
            :model-value="config.entity_identifier_column"
            :options="sheetColumns"
            placeholder="-- Select column --"
            @update:model-value="updateConfig('entity_identifier_column', $event)"
          />
          <MappedSelect
            label="Identifier type"
            :model-value="config.entity_identifier_type"
            :options="identifierTypes"
            placeholder="-- Select type --"
            @update:model-value="updateConfig('entity_identifier_type', $event)"
          />
        </div>
        <p class="text-xs text-gray-500 mt-2">Identifier for the NEW entity being created</p>
      </div>

      <!-- Parent Entity Identifier -->
      <div class="p-3 bg-gray-50 rounded border border-gray-100">
        <h4 class="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">
          Parent entity identifier *
        </h4>
        <div class="grid grid-cols-2 gap-3 mb-3">
          <MappedSelect
            label="Identifier column"
            :model-value="config.parent_identifier_column"
            :options="sheetColumns"
            placeholder="-- Select column --"
            @update:model-value="updateConfig('parent_identifier_column', $event)"
          />
          <MappedSelect
            label="Identifier type"
            :model-value="config.parent_identifier_type"
            :options="identifierTypes"
            placeholder="-- Select type --"
            @update:model-value="updateConfig('parent_identifier_type', $event)"
          />
        </div>
        <p class="text-xs text-gray-500 mb-3">Which existing Entity is the parent?</p>

        <FuzzyMatchControl
          label="Enable fuzzy matching for parent identifiers"
          :enabled="config.use_parent_fuzzy_matching"
          :threshold="config.parent_fuzzy_threshold"
          @update:enabled="updateConfig('use_parent_fuzzy_matching', $event)"
          @update:threshold="updateConfig('parent_fuzzy_threshold', $event)"
        />
      </div>

      <!-- Entity Attributes Mapping (Optional) -->
      <div v-if="entityAttributes.length > 0" class="p-3 bg-gray-50 rounded border border-gray-100">
        <h4 class="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-1">
          Entity attributes (optional)
        </h4>
        <p class="text-xs text-gray-500 mb-2">Map columns to entity attributes. Leave blank to skip.</p>
        <div class="space-y-1.5">
          <div
            v-for="attribute in entityAttributes"
            :key="attribute.id"
            class="grid grid-cols-3 gap-4 items-center px-3 py-2 bg-white rounded border border-gray-100"
          >
            <div>
              <span class="text-sm font-medium text-gray-900">{{ attribute.attribute_name }}</span>
              <span v-if="attribute.is_required" class="text-red-500 text-xs ml-1">*</span>
              <p class="text-xs text-gray-500">{{ attribute.data_type }}</p>
            </div>
            <div class="col-span-2">
              <MappedSelect
                :model-value="config.attribute_mappings[attribute.id]"
                :options="sheetColumns"
                placeholder="-- No mapping --"
                @update:model-value="updateAttributeMapping(attribute.id, $event)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MappedSelect from './controls/MappedSelect.vue';
import FuzzyMatchControl from './controls/FuzzyMatchControl.vue';

export default {
  name: 'EntityImportConfig',
  components: { MappedSelect, FuzzyMatchControl },

  props: {
    config: {
      type: Object,
      required: true
    },
    entityTypes: {
      type: Array,
      required: true
    },
    entityAttributes: {
      type: Array,
      required: true
    },
    sheetColumns: {
      type: Array,
      required: true
    },
    identifierTypes: {
      type: Array,
      required: true
    }
  },

  emits: ['update:config', 'load-attributes'],

  computed: {
    entityTypeOptions() {
      return this.entityTypes.map((t) => ({ value: t.id, label: t.label }));
    },
  },

  methods: {
    updateConfig(field, value) {
      this.$emit('update:config', { ...this.config, [field]: value });
    },

    handleEntityTypeChange(value) {
      this.updateConfig('entity_type_id', value);
      this.$emit('load-attributes');
    },

    updateAttributeMapping(attributeId, columnValue) {
      const newMappings = { ...this.config.attribute_mappings, [attributeId]: columnValue };
      this.$emit('update:config', { ...this.config, attribute_mappings: newMappings });
    }
  }
};
</script>
